import {
  createContext,
  useRef,
  forwardRef,
  useLayoutEffect,
  type HTMLAttributes,
  type DOMAttributes,
  type MutableRefObject,
  type ForwardedRef,
} from 'react';
import * as React from 'react';
import { Overlay, useModalOverlay } from 'react-aria';
import { useOverlayTriggerState } from 'react-stately';

type Props = {
  onDismiss?: () => void;
  isOpen: boolean;
  'data-testid'?: string;
  initialFocusRef?: MutableRefObject<HTMLElement | null>;
} & HTMLAttributes<HTMLDivElement>;

export interface ModalContext {
  modalProps: DOMAttributes<Element>;
  ref: React.MutableRefObject<HTMLDivElement | null>;
}

// This is private context
// for ModalOverlay + ModalContent.
// Do not import and use directly.
export const ModalContext = createContext<ModalContext | null>(null);

export const ModalOverlay = forwardRef(
  (props: Props, forwardedRef: ForwardedRef<HTMLDivElement>) => {
    const { children, onDismiss, isOpen, initialFocusRef, ...divProps } = props;
    const testid = props['data-testid'];
    const ref = useRef<HTMLDivElement | null>(null);
    const overlayTriggerState = useOverlayTriggerState({
      isOpen,
      onOpenChange(isOpen) {
        if (isOpen === true) {
          return;
        }
        onDismiss?.();
      },
    });

    const { modalProps, underlayProps } = useModalOverlay(
      {
        isDismissable: true,
      },
      overlayTriggerState,
      ref
    );

    useLayoutEffect(() => {
      if (
        initialFocusRef?.current === undefined ||
        initialFocusRef?.current === null
      ) {
        return;
      }
      initialFocusRef.current.focus();
    }, []);
    if (isOpen === false) {
      return null;
    }

    return (
      <Overlay>
        <div
          ref={forwardedRef}
          data-testid={testid}
          {...divProps}
          {...underlayProps}
        >
          <ModalContext.Provider value={{ modalProps, ref }}>
            {children}
          </ModalContext.Provider>
        </div>
      </Overlay>
    );
  }
);
